import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Broom, Warning } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/**
 * Offers to remove the sample data a new account starts with.
 *
 * Renders nothing unless the account actually holds demo rows, so it shows up
 * once during onboarding and is gone for good afterwards — there is no point
 * offering to delete something that is not there. Accounts seeded before demo
 * rows were tagged report none and so never see it, which is deliberate: their
 * demo data cannot be told apart from real transactions.
 *
 * It asks the server rather than inferring from the ledger, because "has demo
 * data" is a question about row provenance that only the backend can answer.
 *
 * @param onDeleted called after a successful delete, so the host page can
 *   reload whatever the removal just changed.
 * @param variant "banner" makes the demo state prominent on the dashboard;
 *   "settings" owns the whole Settings card so it disappears with the data.
 */
export default function DeleteDemoData({ onDeleted, className = "", variant = "button" }) {
  const [count, setCount] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/demo");
      setCount(res.data.count);
    } catch {
      // Never let a failed probe put a destructive button on screen.
      setCount(0);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async () => {
    setDeleting(true);
    try {
      const res = await api.post("/demo/clear");
      toast.success(`Removed ${res.data.deleted} demo transactions`);
      setCount(0);
      onDeleted?.();
    } catch {
      toast.error("Could not remove the demo data");
    } finally {
      setDeleting(false);
    }
  };

  if (!count) return null;

  const actionButton = (
    <Button
      variant="outline"
      className={variant === "banner"
        ? "ml-12 shrink-0 border-amber-300/65 bg-amber-300/10 text-amber-100 hover:border-amber-300/85 hover:bg-amber-300/20 hover:text-amber-50 sm:ml-0"
        : `bg-card border-border gap-1.5 ${className}`}
      disabled={deleting}
      data-testid="delete-demo-btn"
    >
      <Broom size={15} />
      {deleting ? "Removing…" : "Delete demo data"}
    </Button>
  );

  const trigger = variant === "banner" ? (
    <div
      role="status"
      data-testid="demo-data-banner"
      className={`relative overflow-hidden rounded-lg border border-amber-400/45 bg-amber-400/[0.08] px-4 py-4 sm:px-5 ${className}`}
    >
      <div className="absolute inset-y-0 left-0 w-1 bg-amber-400" aria-hidden="true" />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-400/15 text-amber-300">
            <Warning size={20} weight="fill" aria-hidden="true" />
          </span>
          <div>
            <p className="font-heading text-sm font-bold tracking-tight text-amber-100">
              You are viewing demo data
            </p>
            <p className="mt-1 max-w-3xl text-sm leading-relaxed text-amber-100/70">
              These {count} sample transactions are here so you can explore FinFlo.
              Delete them when you are ready to add your own records.
            </p>
          </div>
        </div>
        <AlertDialogTrigger asChild>{actionButton}</AlertDialogTrigger>
      </div>
    </div>
  ) : variant === "settings" ? (
    <Card
      className="bg-card border-border p-5 rounded-lg"
      data-testid="demo-data-card"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-heading font-bold tracking-tight">Demo data</h3>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Remove the {count} sample transactions that came with your account.
            Anything you added yourself stays.
          </p>
        </div>
        <AlertDialogTrigger asChild>{actionButton}</AlertDialogTrigger>
      </div>
    </Card>
  ) : (
    <AlertDialogTrigger asChild>{actionButton}</AlertDialogTrigger>
  );

  return (
    <AlertDialog>
      {trigger}
      <AlertDialogContent className="bg-card border-border">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete the {count} sample transactions?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes only the demo data your account started with. Anything
            you have added yourself is kept, as are your categories. You will
            not be offered this again, and it cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-demo-cancel">Keep it</AlertDialogCancel>
          <AlertDialogAction onClick={remove} data-testid="delete-demo-confirm">
            Delete demo data
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
